import EditRubricForm from 'components/Rubrics/EditRubricForm';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import rubricService from 'services/rubric';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    ResponsiveDialog,
    Stack,
    styled,
    Text,
} from 'shell/components';
import { RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getWordCount, isValidRate, roundCriteriaOptionRates, sortCriteria } from 'utils/helper';
import {
    isUnscoredRubric,
    normalizeRubricScoringMode,
    prepareUnscoredRubricCriteria,
    RubricScoringMode,
} from 'utils/rubricScoring';
import { v4 as uuid } from 'uuid';

const getDefaultForm = (t) => ({
    title: '',
    criteria: [
        {
            id: uuid(),
            title: t('rubric.form.title-default', 'Criterion Name'),
            description: t('rubric.form.description-default', 'Description'),
            options: [
                {
                    id: uuid(),
                    title: '',
                    description: '',
                    rate: '',
                },
                {
                    id: uuid(),
                    title: '',
                    description: '',
                    rate: '',
                },
            ],
        },
    ],
    sort: 'desc',
    scoring_mode: RubricScoringMode.SCORED,
});

const defaultForm = {
    title: '',
    criteria: [],
    sort: 'desc',
    scoring_mode: RubricScoringMode.SCORED,
};

const defaultErrors = {
    title: '',
    criteria: {},
};

const EditRubricDialog = ({
    open,
    assignment,
    rubric,
    customDefaultForm,
    requireCriteria = false,
    onClose,
    onSave,
}) => {
    const { setRubrics } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState(defaultErrors);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [hasClickedSaveAsTemplate, setHasClickedSaveAsTemplate] = useState(false);
    const isUnscored = isUnscoredRubric(form);

    const handleClose = () => {
        setDialog({
            open: true,
            message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'common:dialog.unsaved-changes.description',
                'If you exit without saving, all unsaved changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                onClose();
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const validateTitle = useCallback(() => {
        const error = form.title.trim() ? '' : t('error.empty-name', 'Please enter a name');
        setErrors((current) => {
            return { ...current, title: error };
        });
        return !Boolean(error);
    }, [form.title, t]);

    const validateCriteria = useCallback(() => {
        if (requireCriteria && !form.criteria.length) {
            message.error(t('quiz:rubric.require-criterion', 'Add at least one rubric criterion.'));
            return false;
        }
        let error = false;
        form.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isUnscored && !isValidRate(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? t('error.empty-number', 'Please enter a number') : '',
                                description: isDescriptionInvalid
                                    ? t('error.word-count-exceeded', 'Word count exceeded')
                                    : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [form.criteria, isUnscored, message, requireCriteria, t]);

    const validateAll = () => {
        const isTitleValid = validateTitle();
        const areCriteriaValid = validateCriteria();
        if (isTitleValid && areCriteriaValid) return true;
        return false;
    };

    const prepareCriteriaForSave = () => {
        const roundedCriteria = roundCriteriaOptionRates(form.criteria);
        return isUnscored
            ? prepareUnscoredRubricCriteria(roundedCriteria, form.sort)
            : sortCriteria(roundedCriteria, form.sort);
    };

    const handleClickSaveTemplate = () => {
        const isValid = validateAll();
        if (isValid) {
            setHasClickedSaveAsTemplate(true);
            const timestamp = new Date().getTime();
            const criteria = prepareCriteriaForSave();
            const rubric = {
                id: uuid(),
                active: true,
                created_on: timestamp,
                modified_on: timestamp,
                rubric: { ...form, criteria },
                user_id: auth.user_id,
            };
            setRubrics((current) => [...current, rubric]);
            rubricService.createRubric(rubric);
            message.success(t('message.rubric-template-saved-success', 'Rubric was saved as a template.'));
        } else {
            message.error(t('error.invalid-fields', 'One or more fields are not valid.'));
        }
    };

    const handleClickSave = () => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            onSave({ ...form, criteria: prepareCriteriaForSave() });
            onClose();
        } else {
            message.error(t('error.invalid-fields', 'One or more fields are not valid.'));
        }
    };

    useEffect(() => {
        if (open) {
            if (rubric) {
                const normalizedRubric = normalizeRubricScoringMode({ ...(assignment ?? {}), rubric });
                const scoringMode = normalizedRubric.scoring_mode ?? RubricScoringMode.SCORED;
                setForm({
                    ...normalizedRubric,
                    scoring_mode: scoringMode,
                    sort:
                        normalizedRubric.sort ??
                        (scoringMode === RubricScoringMode.UNSCORED ? 'asc' : defaultForm.sort),
                });
            } else if (customDefaultForm) {
                const normalizedRubric = normalizeRubricScoringMode(customDefaultForm);
                const scoringMode = normalizedRubric.scoring_mode ?? RubricScoringMode.SCORED;
                setForm({
                    ...normalizedRubric,
                    scoring_mode: scoringMode,
                    sort:
                        normalizedRubric.sort ??
                        (scoringMode === RubricScoringMode.UNSCORED ? 'asc' : defaultForm.sort),
                });
            } else {
                setForm(getDefaultForm(t));
            }
        }
    }, [assignment, customDefaultForm, open, rubric, t]);

    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                setErrors(defaultErrors);
                setHasClickedSubmit(false);
            }, 150);
        }
    }, [open]);

    useEffect(() => {
        setHasClickedSaveAsTemplate(false);
    }, [form]);

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        {rubric ? t('rubric.title-edit', 'Edit Rubric') : t('rubric.title-create', 'Create a Rubric')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent>
                <EditRubricForm
                    form={form}
                    setForm={setForm}
                    errors={errors}
                    setErrors={setErrors}
                    hasClickedSubmit={hasClickedSubmit}
                    isUnscored={isUnscored}
                    showScoringMode
                    validateTitle={validateTitle}
                    validateCriteria={validateCriteria}
                />
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Stack direction={'row-reverse'} spacing={4}>
                    <Button onClick={handleClickSave}>{t('common:controls.apply', 'Apply')}</Button>
                    <Button
                        disabled={hasClickedSaveAsTemplate}
                        tooltip={{
                            component: t(
                                'rubric.template-tooltip',
                                'This will save the current version of this rubric to your rubric templates, allowing you to use it as a template for future rubrics.'
                            ),
                            maxWidth: '200px',
                        }}
                        variant={'secondary'}
                        onClick={handleClickSaveTemplate}
                    >
                        {t('common:controls.save-as-template', 'Save as a Template')}
                    </Button>
                </Stack>
            </DialogActions>
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

export default memo(EditRubricDialog);
